import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { GripVertical, Maximize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { RootState } from '../store';
import RecorderControls from './RecorderControls';
import { setUIMode } from '../recorderSlice';
import { Z_INDEX } from '../../constants/zIndex';
import DisplayPreview from './MediaPreview/Display';
import { streams } from './utils/streamUtils';

const PopupRecorder: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { status, upload } = useSelector((state: RootState) => state.recorder);
  const isBackdropInvisible = status === 'stopped';
  const isSaving = status === 'saving';
  const isRecording = status === 'recording' || status === 'paused';
  const videoTitle =
    upload.title || t('recorder.popupRecorder.title', 'Screen Recording');

  // State for dragging
  const [position, setPosition] = useState({ x: 20, y: 20 }); // Start at top-left with padding
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const popupRef = useRef<HTMLDivElement>(null);
  const [isHovering, setIsHovering] = useState(false);

  // Check if we have video tracks
  const hasVideoTracks =
    streams.displayMediaStream?.getVideoTracks().length > 0;

  // Handle switching back to fullscreen mode
  const handleSwitchToFullscreen = () => {
    dispatch(setUIMode('fullscreen'));
  };

  // Handle drag start
  const handleMouseDown = (e: React.MouseEvent) => {
    if (popupRef.current) {
      setIsDragging(true);
      const rect = popupRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  // Handle drag
  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Ensure the popup stays within the viewport
      const maxX = window.innerWidth - (popupRef.current?.offsetWidth || 0);
      const maxY = window.innerHeight - (popupRef.current?.offsetHeight || 0);

      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    }
  };

  // Handle drag end
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add event listeners for dragging outside the component
  React.useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (isDragging && popupRef.current) {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;

        // Ensure the popup stays within the viewport with some padding
        const maxX = window.innerWidth - popupRef.current.offsetWidth;
        const maxY = window.innerHeight - popupRef.current.offsetHeight;

        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY)),
        });
      }
    };

    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleGlobalMouseMove);
      window.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, dragOffset]);

  // Ensure the popup is fully visible on the screen when it mounts
  useEffect(() => {
    if (popupRef.current) {
      const popupWidth = popupRef.current.offsetWidth;
      const popupHeight = popupRef.current.offsetHeight;

      // Calculate the maximum x and y positions to keep the popup fully visible
      const maxX = window.innerWidth - popupWidth - 20; // 20px padding from right edge
      const maxY = window.innerHeight - popupHeight - 20; // 20px padding from bottom edge

      // Set the initial position to be centered on the screen
      const centerX = Math.max(
        20,
        Math.min((window.innerWidth - popupWidth) / 2, maxX),
      );
      const centerY = Math.max(
        20,
        Math.min((window.innerHeight - popupHeight) / 2, maxY),
      );

      setPosition({ x: centerX, y: centerY });
    }
  }, []);

  // Handle window resize to keep popup within viewport
  useEffect(() => {
    const handleResize = () => {
      if (popupRef.current) {
        const popupWidth = popupRef.current.offsetWidth;
        const popupHeight = popupRef.current.offsetHeight;

        // Calculate the maximum x and y positions
        const maxX = window.innerWidth - popupWidth;
        const maxY = window.innerHeight - popupHeight;

        // Adjust position if needed to keep popup within viewport
        setPosition((prevPosition) => ({
          x: Math.min(prevPosition.x, maxX),
          y: Math.min(prevPosition.y, maxY),
        }));
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div
      ref={popupRef}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      className={cn(
        'fixed w-[400px] max-w-[calc(100vw-40px)] rounded-xl overflow-hidden transition-all duration-300 shadow-lg border',
        isBackdropInvisible && !isSaving ? 'opacity-0' : 'opacity-100',
        isDragging ? 'cursor-grabbing scale-[0.98] shadow-2xl' : 'cursor-auto',
        isHovering && !isDragging && 'scale-[1.01] shadow-2xl',
        'border-border',
      )}
      style={{
        left: position.x,
        top: position.y,
        zIndex: Z_INDEX.popover,
      }}
    >
      {/* Draggable header */}
      <div
        role="button"
        tabIndex={0}
        className={cn(
          'flex items-center justify-between bg-primary text-primary-foreground p-3 border-b transition-colors duration-200 hover:bg-primary/90',
          isDragging ? 'cursor-grabbing' : 'cursor-grab',
        )}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
          }
        }}
        aria-label="Draggable recorder window header"
      >
        <div className="flex items-center overflow-hidden">
          <GripVertical className="h-4 w-4 opacity-80 flex-shrink-0" />
          <span className="ml-1.5 font-medium whitespace-nowrap overflow-hidden text-ellipsis max-w-[280px] text-sm tracking-wide">
            {videoTitle}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSwitchToFullscreen}
          className="text-primary-foreground opacity-80 transition-all duration-200 hover:opacity-100 hover:scale-110 hover:bg-white/10 h-8 w-8 p-0"
        >
          <Maximize2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Video preview (only shown when recording) */}
      {isRecording && hasVideoTracks && (
        <div className="w-full h-[225px] overflow-hidden bg-black relative">
          <div className="absolute inset-0 shadow-[inset_0_0_20px_rgba(0,0,0,0.3)] pointer-events-none" />
          <div className="w-full h-full flex items-center justify-center">
            <DisplayPreview />
          </div>
        </div>
      )}

      {/* Recorder controls */}
      <div className="p-1.5 bg-muted/90">
        <RecorderControls
          onToggleNotes={() => {}}
          showNotes={false}
          isPopupMode
        />
      </div>
    </div>
  );
};

export default PopupRecorder;
